/* Native enhancements survive editor reloads through custom-element lifecycle. */
class JomVideo extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.querySelector('button')?.addEventListener('click', () => {
      const frame = document.createElement('iframe');
      const id = encodeURIComponent(this.dataset.videoId);
      frame.src = this.dataset.provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1` : `https://player.vimeo.com/video/${id}?autoplay=1`;
      frame.title = this.dataset.label;
      frame.allow = 'autoplay; fullscreen; picture-in-picture';
      frame.allowFullscreen = true;
      this.replaceChildren(frame);
    });
  }
}
class JomProductGrid extends HTMLElement {
  connectedCallback() { this.load(); }
  disconnectedCallback() { this.controller?.abort(); }
  async load() {
    if (this.loaded || !['recommendations','recent'].includes(this.dataset.mode)) return;
    this.loaded = true;
    this.controller = new AbortController();
    const grid = this.querySelector('[data-product-grid]');
    if (!grid) return;
    try {
      if (this.dataset.mode === 'recommendations') {
        if (this.dataset.ready === 'true' || !this.dataset.productId) return;
        const url = new URL(this.dataset.url, location.origin);
        url.search = new URLSearchParams({section_id:this.dataset.sectionId, product_id:this.dataset.productId, limit:this.dataset.limit, intent:'related'});
        const response = await fetch(url, {signal:this.controller.signal});
        if (!response.ok) throw new Error('Recommendations unavailable');
        const dom = new DOMParser().parseFromString(await response.text(),'text/html');
        const content = dom.querySelector('[data-product-grid]');
        if (content) grid.replaceChildren(...content.children);
      } else {
        grid.replaceChildren();
        let handles = [];
        try { handles = JSON.parse(localStorage.getItem('jom-recent-products') || '[]'); } catch { /* storage is optional */ }
        if (!Array.isArray(handles)) handles=[];
        const current = document.querySelector('[data-jom-current-product]')?.dataset.jomCurrentProduct;
        handles = handles.filter(h => typeof h === 'string' && /^[a-z0-9-]+$/.test(h) && h !== current).slice(0,Number(this.dataset.limit));
        const cards = await Promise.all(handles.map(async handle => {
          const url = new URL(`${this.dataset.root.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}`,location.origin);
          url.searchParams.set('section_id',this.dataset.sectionId);
          const response = await fetch(url,{signal:this.controller.signal});
          if (!response.ok) return null;
          return new DOMParser().parseFromString(await response.text(),'text/html').querySelector('[data-jom-recent-card]');
        }));
        grid.replaceChildren(...cards.filter(Boolean));
      }
      const section = this.closest('[data-jom-library]');
      if (section) section.hidden = !grid.children.length && !window.Shopify?.designMode;
    } catch (error) { if (error.name !== 'AbortError' && !window.Shopify?.designMode) this.closest('[data-jom-library]').hidden = true; }
  }
}
customElements.define('jom-video',JomVideo);
customElements.define('jom-product-grid',JomProductGrid);
function initLibrary(root = document) {
  root.querySelectorAll('[data-jom-scroller]').forEach(scroller => {
    if (!scroller.classList.contains('jom-lib-swipe') || scroller.dataset.wired) return;
    scroller.dataset.wired='true';
    const controls=document.createElement('div');controls.className='jom-lib-slider-controls';
    for (const [dir,label] of [[-1,'←'],[1,'→']]) {
      const button=document.createElement('button');button.type='button';button.textContent=label;
      button.setAttribute('aria-label',dir<0 ? scroller.dataset.previous : scroller.dataset.next);
      button.addEventListener('click',()=>scroller.scrollBy({left:dir*scroller.clientWidth*.82,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'}));controls.append(button);
    }
    scroller.after(controls);
  });
}
document.addEventListener('DOMContentLoaded',()=>{
  initLibrary();
  const current=document.querySelector('[data-jom-current-product]')?.dataset.jomCurrentProduct;
  if (current) { try { let previous=JSON.parse(localStorage.getItem('jom-recent-products')||'[]'); if(!Array.isArray(previous))previous=[];localStorage.setItem('jom-recent-products',JSON.stringify([current,...previous.filter(h=>typeof h==='string'&&h!==current)].slice(0,12))); } catch { /* storefront remains usable without localStorage */ } }
});
document.addEventListener('shopify:section:load',event=>initLibrary(event.target));
class JomReviewList extends HTMLElement {
  connectedCallback() {
    this.page=0;
    this.observer=new MutationObserver(()=>this.update());
    this.observer.observe(this,{childList:true,subtree:true});this.update();
  }
  disconnectedCallback() { this.observer?.disconnect(); }
  update() {
    const list=this.querySelector('.jdgm-rev-widg__reviews');
    if (!list) return;
    const cards=[...list.children].filter(c=>c.classList.contains('jdgm-rev'));
    if (!cards.length) return;
    const signature=cards.map(c=>c.dataset.reviewId||c.textContent.slice(0,80)).join('|');
    if (signature!==this.signature) { this.signature=signature;this.page=0; }
    const count=Number(this.dataset.count)||3;
    if (!this.controls) {
      this.controls=document.createElement('div');this.controls.className='jom-lib-slider-controls';
      for (const [direction,label] of [[-1,'←'],[1,'→']]) {
        const button=document.createElement('button');button.type='button';button.textContent=label;
        button.setAttribute('aria-label',direction<0?this.dataset.previous:this.dataset.next);
        button.addEventListener('click',()=>{
          if (this.dataset.layout==='slider')list.scrollBy({left:direction*list.clientWidth*.9,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
          else {this.page+=direction;this.update();}
        });this.controls.append(button);
      }
      list.after(this.controls);
    }
    if(this.dataset.layout==='slider'){
      cards.forEach(c=>c.hidden=false);list.style.setProperty('--lib-review-columns',String(Math.min(count,4)));
      this.controls.hidden=cards.length<2;
    }else{
      list.style.gridTemplateColumns=`repeat(${Math.min(count,3)},minmax(0,1fr))`;
      this.page=Math.max(0,Math.min(this.page,Math.ceil(cards.length/count)-1));
      cards.forEach((c,i)=>c.hidden=Math.floor(i/count)!==this.page);
      this.controls.hidden=cards.length<=count;
      this.controls.children[0].disabled=this.page===0;this.controls.children[1].disabled=(this.page+1)*count>=cards.length;
    }
  }
}
customElements.define('jom-review-list',JomReviewList);
